import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const NAV_ITEMS = {
  team: [
    { path: '/scaling/team#overview',    icon: '⬡',  label: 'Overview' },
    { path: '/scaling/team#companies',   icon: '🏢', label: 'Companies' },
    { path: '/scaling/team#coaches',     icon: '👥', label: 'Coaches' },
    { path: '/scaling/team#config',      icon: '⚙️', label: 'Config' },
    { path: '/scaling/team#programmes',  icon: '🗂', label: 'Programmes' },
    { path: '/scaling/team#reports',     icon: '📋', label: 'Reports' },
  ],
  coach: [
    { path: '/scaling/coach',            icon: '⬡', label: 'My Companies' },
    { path: '/scaling/coach/sessions',   icon: '📅', label: 'Sessions' },
    { path: '/scaling/coach/assessments',icon: '📊', label: 'Assessments' },
  ],
  beneficiary: [
    { path: '/scaling/company',          icon: '⬡', label: 'Dashboard' },
    { path: '/scaling/company/assessment',icon:'📊', label: 'Assessment' },
    { path: '/scaling/company/canvas',   icon: '🗂', label: 'Canvas' },
    { path: '/scaling/company/journey',  icon: '🗺', label: 'Journey' },
    { path: '/scaling/company/sessions', icon: '📅', label: 'Sessions' },
    { path: '/scaling/company/documents',icon: '📎', label: 'Documents' },
    { path: '/scaling/company/messages', icon: '💬', label: 'Messages' },
  ],
  client: [
    { path: '/scaling/client#overview',  icon: '⬡', label: 'Overview' },
    { path: '/scaling/client#companies', icon: '🏢', label: 'Companies' },
    { path: '/scaling/client#reports',   icon: '📋', label: 'Reports' },
  ],
};

const ROLE_LABELS = {
  team:        'Mirepa Team',
  coach:       'Coach',
  beneficiary: 'Participant',
  client:      'Programme Partner',
};

export default function DashboardLayout({ children }) {
  const { profile, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const role = profile?.role || 'beneficiary';
  const navItems = NAV_ITEMS[role] || [];
  const useMobileNav = ['beneficiary', 'client'].includes(role);
  const bottomNavItems = navItems.slice(0, 5);

  const handleLogout = async () => {
    await logout();
    navigate('/scaling/login');
  };

  const layoutClass = [
    'gm-layout',
    !sidebarOpen ? 'gm-layout--collapsed' : '',
    useMobileNav ? 'gm-layout--mobile-nav' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={layoutClass}>

      {/* Sidebar — always rendered, hidden on mobile for beneficiary/client via CSS */}
      <aside className="gm-sidebar">
        <div className="gm-sidebar-header">
          <div className="gm-sidebar-logos">
            <img src="/logos/gm-logo.png" alt="Growth Mosaic" className="gm-sidebar-logo" />
            {sidebarOpen && (
              <div className="gm-sidebar-brand">
                <span className="gm-sidebar-brand-name">Growth Mosaic</span>
                <span className="gm-sidebar-module">Scaling Readiness</span>
              </div>
            )}
          </div>
          <button
            className="gm-sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? '←' : '→'}
          </button>
        </div>

        {sidebarOpen && (
          <div className="gm-sidebar-role-badge">{ROLE_LABELS[role]}</div>
        )}

        <nav className="gm-sidebar-nav" aria-label="Main navigation">
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path.split('/').length <= 3}
              className={({ isActive }) =>
                `gm-nav-item ${isActive ? 'gm-nav-item--active' : ''}`
              }
            >
              <span className="gm-nav-icon" aria-hidden="true">{item.icon}</span>
              {sidebarOpen && <span className="gm-nav-label">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="gm-sidebar-footer">
          {sidebarOpen && (
            <div className="gm-sidebar-user">
              <div className="gm-sidebar-user-name">{profile?.displayName}</div>
              <div className="gm-sidebar-user-email">{profile?.email}</div>
            </div>
          )}
          <button className="gm-sidebar-logout" onClick={handleLogout} title="Sign out">
            <span>⎋</span>
            {sidebarOpen && <span>Sign out</span>}
          </button>
          {sidebarOpen && (
            <div className="gm-sidebar-powered">
              <span>Powered by</span>
              <img src="/logos/mirepa-logo.png" alt="Mirepa" className="gm-logo-mirepa" />
              <div className="gm-powered-by-text">Part of the Mirepa Platform</div>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="gm-main">
        {children}
      </main>

      {/* Bottom nav — mobile only for beneficiary & client */}
      {useMobileNav && (
        <nav className="gm-bottom-nav" aria-label="Mobile navigation">
          {bottomNavItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path.split('/').length <= 3}
              className={({ isActive }) =>
                `gm-bottom-nav-item ${isActive ? 'gm-bottom-nav-item--active' : ''}`
              }
            >
              <span aria-hidden="true">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
          <button className="gm-bottom-nav-item" onClick={handleLogout} title="Sign out">
            <span>⎋</span>
            <span>Sign out</span>
          </button>
        </nav>
      )}
    </div>
  );
}
